//changed due to package upgrade
import React, { useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import IntlMessages from '@projectK/helpers/IntlMessages';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import AppGridContainer from '@projectK/components/AppGridContainer';
import { alpha, Autocomplete, Grid, IconButton } from '@mui/material';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import jwtAxios from '@projectK/services/auth/JWT';
import { CloseRounded } from '@mui/icons-material';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import AppModalConfirmation from '@projectK/components/AppModalConfirmation';
import PropTypes from 'prop-types';
import { isAdmin } from '@projectK/context/RolesProvider';

const validationSchema = yup.object().shape({
  members: yup
    .array()
    .of(yup.string().nullable().required(<IntlMessages id='validation.memberRequired' />))
    .min(1, () => <IntlMessages id='validation.chooseMember' />),
});

const customScrollbarStyle = {
  WebkitScrollbar: {
    width: '12px',
  },
  WebkitScrollbarThumb: {
    backgroundColor: '#888',
  },
  WebkitScrollbarThumbHover: {
    backgroundColor: '#555',
  },
};

const AddExisitingAccountMember = ({ isOpen, onClose, fetchMembersData }) => {
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const { user } = useAuthUser();
  const [users, setUsers] = useState([]);
  const [searchTermMember, setSearchTermMember] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  useEffect(() => {
    if(isAdmin(user)){
      fetchUsersData();
    }
  }, [searchTermMember]);

  const fetchUsersData = async () => {
    try {
      const response = await jwtAxios.get(`/users`, {
        params: {
          perPage: 'all',
          search: searchTermMember,
          filters: {
            roles: 'parent',
          },
          is_active: 1,
        },
      });
      const { data } = response;
      setUsers(data.list.filter((item) => item.role_ids.length === 1));
    } catch (error) {
      setUsers([]);
    }
  };

  const handleDialogOpen = () => {
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleCloseModalDialog = () => {
    setOpenDialog(false); // to close the dialog
    handleModalClose(); //to close the modal
  };
  const handleModalClose = () => {
    onClose();
  };
  return (
    <>
      <Modal open={isOpen} onClose={handleDialogOpen}>
        <Box
          className='p-popup'
          sx={{
            bgcolor: 'background.paper',
            ...customScrollbarStyle,
          }}
        >
          <Formik
            initialValues={{
              members: [],
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                await Promise.all(
                  values?.members?.map(async (member_id) => {
                    jwtAxios
                      .post(`/users/${member_id}/addmember/${user.id}`)
                      .then((response) => {
                        setSubmitting(false);
                        handleCloseModalDialog();
                        fetchMembersData();
                        infoViewActionsContext.showMessage(
                          'Parent added successfully!',
                        );
                      })
                      .catch((error) => {
                        infoViewActionsContext.fetchError(
                          error.response.data.error,
                        );
                        setSubmitting(false);
                      });
                  }),
                );
              } catch (error) {
                infoViewActionsContext.fetchError(error.response.data.error);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              isSubmitting,
              values,
              errors,
              touched,
              setFieldValue,
              setFieldTouched,
            }) => (
              <Form noValidate autoComplete='off'>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography
                      variant='h4'
                      sx={{
                        color: companyTheme
                          ? companyTheme.primary.main
                          : 'text.primary',
                      }}
                    >
                      Add Parent Members
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      size='small'
                      sx={{ fontWeight: 'bold', color: '#000000' }}
                      className='icon-btn'
                      onClick={handleDialogOpen}
                    >
                      <CloseRounded />
                    </IconButton>
                  </Box>
                </Box>
                <AppGridContainer>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <Box
                      sx={{
                        border: '1px solid #EAECF0',
                        p: 6,
                        borderRadius: 3,
                        boxShadow:
                          '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                      }}
                    >
                      <AppGridContainer>
                        <Grid size={{ xs: 12, md: 12 }}>
                          <Typography mb={2}>
                            This will only search for members
                          </Typography>

                          <Grid size={{ xs: 12, md: 12 }}>
                            <Autocomplete
                              multiple
                              id='free-solo-2-demo'
                              disableClearable
                              options={(users || []).sort((a, b) =>
                                a.first_name.localeCompare(b.first_name),
                              )}
                              onChange={(event, newValue) => {
                                setFieldValue(
                                  'members',
                                  newValue.map((member) => member.id),
                                );
                              }}
                              onInputChange={(_, value) =>
                                setSearchTermMember(value)
                              }
                              onBlur={() => setFieldTouched('user_id', true)}
                              getOptionLabel={(option) =>
                                `${option.first_name} ${option.last_name} - ${option.email}`
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label='Search Members'
                                  name='members'
                                  value={values.members}
                                  error={touched.members && !!errors.members}
                                  helperText={touched.members && errors.members}
                                  InputProps={{// NOSONAR - MUI v6 Autocomplete still requires InputProps for dropdown anchor
                                    ...params.InputProps,
                                    type: 'search',
                                  }}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>

                        <Grid size={{ xs: 12, md: 12 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                backgroundColor: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                                '&:hover': {
                                  backgroundColor: companyTheme
                                    ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                                    : alpha('#2196f3', 0.8),
                                },
                              }}
                              variant='contained'
                              type='submit'
                            >
                              <IntlMessages id='common.save' />
                            </Button>
                            <Button
                              sx={{
                                position: 'relative',
                                minWidth: 100,
                                ml: 2.5,
                                borderColor: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                                '&:hover': {
                                  borderColor: companyTheme
                                    ? alpha(companyTheme.primary.main, 0.8)
                                    : alpha('#2196f3', 0.8),
                                },
                                color: companyTheme
                                  ? companyTheme.primary.main
                                  : '#2196f3',
                              }}
                              variant='outlined'
                              onClick={handleDialogOpen}
                            >
                              <IntlMessages id='common.cancel' />
                            </Button>
                          </Box>
                        </Grid>
                      </AppGridContainer>
                    </Box>
                  </Grid>
                </AppGridContainer>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
      <AppModalConfirmation
        openDialog={openDialog}
        handleCloseDialog={handleCloseDialog}
        handleCloseModalDialog={handleCloseModalDialog}
      />
    </>
  );
};

export default AddExisitingAccountMember;
AddExisitingAccountMember.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  fetchMembersData: PropTypes.func,
};
